/**
 * B2–B4 — a aba "Página" da gaveta do redirecionador: a página de passagem OPCIONAL.
 *
 * Desligada por padrão: o 302 direto continua o caminho de sempre. Tudo que esta aba grava
 * é cobrado de novo no servidor (zod `.strict()`, formato de cada ID, permissão
 * `redirector:page` = OWNER/ADMIN, auditoria com valor antigo e novo). A tela só ajuda a
 * preencher certo e mostra o que o servidor devolveu:
 *
 *  - **o A/B é sorteado no servidor** (B2.6). Aqui só se escolhe a proporção;
 *  - **pixels são só IDs** (B2.3): nada de colar script;
 *  - **a imagem nunca é URL** (B4.1): envio de arquivo (o servidor re-escreve) ou "Gerar";
 *  - **o placar PAGE × DIRECT chega pronto** (invariante 3): taxa e diferença do servidor.
 */
import {
  Badge,
  Button,
  Checkbox,
  ConfidenceBadge,
  EmptyState,
  ErrorState,
  Field,
  FormAlert,
  Input,
  NativeSelect,
  Skeleton,
  SkeletonText,
  formatNumber,
  useToast,
} from '@mgdlead/ui';
import { useRef, useState } from 'react';
import { Copy, ImagePlus, QrCode, Sparkles, Trash2 } from 'lucide-react';

import {
  AVISO_CACHE_WHATSAPP,
  OG_DESCRIPTION_MAX,
  OG_DESCRIPTION_RECOMMENDED,
  OG_TITLE_MAX,
  OG_TITLE_RECOMMENDED,
  PERCENT_OPTIONS,
  PIXEL_EVENTS,
  TITULO_PADRAO,
  VARIANTE,
  contador,
  estadoDaPagina,
  hostDe,
  leituraDoPlacar,
  prepararImagem,
  taxaEmTexto,
} from '../../../features/redirector/landing-ui';
import {
  useGenerateRedirectorPageImage,
  useRedirectorPage,
  useRedirectorPageImage,
  useRedirectorPageVariants,
  useRemoveRedirectorPageImage,
  useUpdateRedirectorPage,
  useUploadRedirectorPageImage,
} from '../../../lib/api';
import type {
  RedirectorPage,
  RedirectorPageVariantRow,
  RedirectorPageVariants,
} from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';

export interface LandingPageTabProps {
  workspaceId: string;
  redirectorId: string;
  publicUrl: string | null;
  /** `redirector:page` — OWNER/ADMIN. Esconder é UX; quem barra é o servidor. */
  canEdit: boolean;
}

type PixelEvent = RedirectorPage['metaPixelEvents'][number];

/** O rascunho do formulário: tudo string/array, como os campos editam. */
type Rascunho = {
  enabled: boolean;
  pagePercent: string;
  ogTitle: string;
  ogDescription: string;
  metaPixelId: string;
  metaPixelEvents: PixelEvent[];
  metaPixelClickEvent: string;
  ga4MeasurementId: string;
  gtmContainerId: string;
  pinterestTagId: string;
};

function rascunhoDe(page: RedirectorPage): Rascunho {
  return {
    enabled: page.enabled,
    pagePercent: String(page.pagePercent),
    ogTitle: page.ogTitle ?? '',
    ogDescription: page.ogDescription ?? '',
    metaPixelId: page.metaPixelId ?? '',
    metaPixelEvents: [...page.metaPixelEvents],
    metaPixelClickEvent: page.metaPixelClickEvent ?? '',
    ga4MeasurementId: page.ga4MeasurementId ?? '',
    gtmContainerId: page.gtmContainerId ?? '',
    pinterestTagId: page.pinterestTagId ?? '',
  };
}

/** Campo vazio vira `null` (= limpar no servidor); o resto vai como digitado (trim). */
function ouNull(valor: string): string | null {
  const limpo = valor.trim();
  return limpo.length === 0 ? null : limpo;
}

export function LandingPageTab({
  workspaceId,
  redirectorId,
  publicUrl,
  canEdit,
}: LandingPageTabProps) {
  const pageQuery = useRedirectorPage(workspaceId, redirectorId);

  if (pageQuery.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <SkeletonText lines={2} />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (pageQuery.isError) {
    return (
      <ErrorState
        title="Não conseguimos carregar a página de passagem"
        description={humanizeError(pageQuery.error)}
        onRetry={() => void pageQuery.refetch()}
      />
    );
  }
  return (
    <EditorDaPagina
      // A chave reinicia o rascunho quando o servidor devolve uma versão nova.
      key={`${pageQuery.data.redirectorId}:${String(pageQuery.data.updatedAt)}`}
      workspaceId={workspaceId}
      page={pageQuery.data}
      publicUrl={publicUrl}
      canEdit={canEdit}
    />
  );
}

function EditorDaPagina({
  workspaceId,
  page,
  publicUrl,
  canEdit,
}: {
  workspaceId: string;
  page: RedirectorPage;
  publicUrl: string | null;
  canEdit: boolean;
}) {
  const { toast } = useToast();
  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoDe(page));
  const [erro, setErro] = useState<string | null>(null);
  const update = useUpdateRedirectorPage();

  const set = <K extends keyof Rascunho>(campo: K, valor: Rascunho[K]) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const titulo = contador(rascunho.ogTitle, OG_TITLE_RECOMMENDED, OG_TITLE_MAX);
  const descricao = contador(
    rascunho.ogDescription,
    OG_DESCRIPTION_RECOMMENDED,
    OG_DESCRIPTION_MAX,
  );
  const excedido = titulo.estado === 'excedido' || descricao.estado === 'excedido';

  const salvar = () => {
    setErro(null);
    update.mutate(
      {
        workspaceId,
        redirectorId: page.redirectorId,
        enabled: rascunho.enabled,
        pagePercent: Number(rascunho.pagePercent),
        ogTitle: ouNull(rascunho.ogTitle),
        ogDescription: ouNull(rascunho.ogDescription),
        metaPixelId: ouNull(rascunho.metaPixelId),
        metaPixelEvents: rascunho.metaPixelEvents,
        metaPixelClickEvent:
          rascunho.metaPixelClickEvent.length === 0
            ? null
            : (rascunho.metaPixelClickEvent as PixelEvent),
        ga4MeasurementId: ouNull(rascunho.ga4MeasurementId.toUpperCase()),
        gtmContainerId: ouNull(rascunho.gtmContainerId.toUpperCase()),
        pinterestTagId: ouNull(rascunho.pinterestTagId),
      },
      {
        onSuccess: () => toast({ title: 'Página de passagem salva', tone: 'success' }),
        onError: (error: unknown) => setErro(humanizeError(error)),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-md font-medium">Página de passagem</h3>
          <Badge tone={page.enabled ? 'success' : 'neutral'} dot>
            {page.enabled ? 'Ligada' : 'Desligada'}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{estadoDaPagina(page)}</p>
        <p className="text-2xs text-muted-foreground">
          No celular, a página abre o WhatsApp direto no botão “Entrar no grupo”, com “Copiar link”
          e “Não abriu? Toque aqui” embaixo. No computador, mostra um QR para entrar pelo celular.
        </p>
        {canEdit ? null : (
          <FormAlert tone="info">
            Só o dono e os administradores do workspace mudam a página de passagem. Você vê como ela
            está configurada.
          </FormAlert>
        )}
      </section>

      <fieldset disabled={!canEdit} className="flex flex-col gap-3 border-t border-border pt-4">
        <Checkbox
          label="Mostrar página antes de abrir o WhatsApp"
          hint="Desligado, o clique vai direto para o grupo — como sempre foi."
          checked={rascunho.enabled}
          onChange={(event) => set('enabled', event.target.checked)}
        />
        {rascunho.enabled ? (
          <Field
            label="Quem vê a página"
            hint="A cada clique sorteamos se a pessoa vê a página ou vai direto. Assim dá para comparar os dois abaixo."
          >
            {(control) => (
              <NativeSelect
                {...control}
                value={rascunho.pagePercent}
                onChange={(event) => set('pagePercent', event.target.value)}
              >
                {PERCENT_OPTIONS.some((o) => o.value === rascunho.pagePercent) ? null : (
                  <option value={rascunho.pagePercent}>
                    {rascunho.pagePercent}% dos cliques veem a página
                  </option>
                )}
                {PERCENT_OPTIONS.map((opcao) => (
                  <option key={opcao.value} value={opcao.value}>
                    {opcao.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        ) : null}
      </fieldset>

      {/* ------------------------------ prévia (B4) ------------------------------ */}
      <fieldset disabled={!canEdit} className="flex flex-col gap-3 border-t border-border pt-4">
        <legend className="sr-only">Prévia do link</legend>
        <h3 className="text-md font-medium">Prévia do link</h3>
        <Field
          label="Título"
          hint={titulo.texto}
          error={titulo.estado === 'excedido' ? titulo.texto : undefined}
        >
          {(control) => (
            <Input
              {...control}
              value={rascunho.ogTitle}
              placeholder={TITULO_PADRAO}
              invalid={titulo.estado === 'excedido'}
              onChange={(event) => set('ogTitle', event.target.value)}
            />
          )}
        </Field>
        <Field
          label="Descrição"
          hint={descricao.texto}
          error={descricao.estado === 'excedido' ? descricao.texto : undefined}
        >
          {(control) => (
            <Input
              {...control}
              value={rascunho.ogDescription}
              placeholder="Toque para entrar no grupo."
              invalid={descricao.estado === 'excedido'}
              onChange={(event) => set('ogDescription', event.target.value)}
            />
          )}
        </Field>
        <ImagemDaPrevia workspaceId={workspaceId} page={page} canEdit={canEdit} onErro={setErro} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PreviaWhatsApp
            workspaceId={workspaceId}
            page={page}
            titulo={rascunho.ogTitle}
            descricao={rascunho.ogDescription}
            publicUrl={publicUrl}
          />
          <PreviaCelular titulo={rascunho.ogTitle} descricao={rascunho.ogDescription} />
        </div>
        <p className="text-2xs text-muted-foreground">{AVISO_CACHE_WHATSAPP}</p>
      </fieldset>

      {/* ------------------------------ pixels (B3) ------------------------------ */}
      <fieldset disabled={!canEdit} className="flex flex-col gap-3 border-t border-border pt-4">
        <legend className="sr-only">Pixels e tags</legend>
        <h3 className="text-md font-medium">Pixels e tags</h3>
        <p className="text-2xs text-muted-foreground">
          Só o número de cada ferramenta — nada de colar código. Eles disparam apenas na página de
          passagem, e nenhum dado pessoal (telefone, IP) vai junto.
        </p>
        <Field label="Pixel da Meta (ID)" hint="Só números, de 10 a 20 dígitos.">
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              value={rascunho.metaPixelId}
              placeholder="123456789012345"
              onChange={(event) => set('metaPixelId', event.target.value)}
            />
          )}
        </Field>
        {rascunho.metaPixelId.trim().length > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Eventos ao abrir a página</span>
            <div className="grid gap-1 sm:grid-cols-2">
              {PIXEL_EVENTS.map((evento) => (
                <Checkbox
                  key={evento.value}
                  label={evento.label}
                  checked={rascunho.metaPixelEvents.includes(evento.value)}
                  onChange={(event) =>
                    set(
                      'metaPixelEvents',
                      event.target.checked
                        ? [...rascunho.metaPixelEvents, evento.value]
                        : rascunho.metaPixelEvents.filter((v) => v !== evento.value),
                    )
                  }
                />
              ))}
            </div>
            <Field label="Evento ao tocar em “Entrar no grupo”">
              {(control) => (
                <NativeSelect
                  {...control}
                  value={rascunho.metaPixelClickEvent}
                  onChange={(event) => set('metaPixelClickEvent', event.target.value)}
                >
                  <option value="">Nenhum</option>
                  {PIXEL_EVENTS.map((evento) => (
                    <option key={evento.value} value={evento.value}>
                      {evento.label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
        ) : null}
        <Field label="Google Analytics 4 (ID de medição)" hint="Começa com G- (ex.: G-ABC123XYZ).">
          {(control) => (
            <Input
              {...control}
              value={rascunho.ga4MeasurementId}
              placeholder="G-ABC123XYZ"
              onChange={(event) => set('ga4MeasurementId', event.target.value)}
            />
          )}
        </Field>
        <Field
          label="Google Tag Manager (contêiner)"
          hint="Começa com GTM-. Fica guardado, mas só dispara quando o GTM for liberado na plataforma (por segurança, a página não carrega tags de terceiros)."
        >
          {(control) => (
            <Input
              {...control}
              value={rascunho.gtmContainerId}
              placeholder="GTM-ABC1234"
              onChange={(event) => set('gtmContainerId', event.target.value)}
            />
          )}
        </Field>
        <Field label="Tag do Pinterest (ID)" hint="Só números, de 10 a 16 dígitos.">
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              value={rascunho.pinterestTagId}
              placeholder="2612345678901"
              onChange={(event) => set('pinterestTagId', event.target.value)}
            />
          )}
        </Field>
      </fieldset>

      {erro === null ? null : (
        <FormAlert tone="danger" title="Não deu para salvar">
          {erro}
        </FormAlert>
      )}
      {canEdit ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={salvar} loading={update.isPending} disabled={excedido}>
            Salvar página
          </Button>
        </div>
      ) : null}

      <PlacarAB workspaceId={workspaceId} redirectorId={page.redirectorId} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Imagem da prévia: enviar (redimensionada aqui, re-escrita no servidor) ou gerar */
/* -------------------------------------------------------------------------- */

function ImagemDaPrevia({
  workspaceId,
  page,
  canEdit,
  onErro,
}: {
  workspaceId: string;
  page: RedirectorPage;
  canEdit: boolean;
  onErro: (erro: string | null) => void;
}) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadRedirectorPageImage();
  const gerar = useGenerateRedirectorPageImage();
  const remover = useRemoveRedirectorPageImage();
  const ref = { workspaceId, redirectorId: page.redirectorId };
  const ocupado = upload.isPending || gerar.isPending || remover.isPending;

  const falhou = (error: unknown) => onErro(humanizeError(error));

  const escolher = async (arquivo: File | undefined) => {
    if (!arquivo) return;
    onErro(null);
    try {
      const base64 = await prepararImagem(arquivo);
      upload.mutate(
        { ...ref, base64 },
        {
          onSuccess: () => toast({ title: 'Imagem da prévia atualizada', tone: 'success' }),
          onError: falhou,
        },
      );
    } catch (error) {
      onErro(error instanceof Error ? error.message : 'Não deu para ler essa imagem.');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">Imagem (1200 × 630)</span>
      <p className="text-2xs text-muted-foreground">
        {page.ogImage === null
          ? 'Sem imagem: o WhatsApp mostra só o título e a descrição.'
          : page.ogImage.source === 'GENERATED'
            ? 'Imagem gerada a partir do título e da descrição salvos.'
            : 'Imagem enviada por você.'}
      </p>
      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            aria-label="Enviar imagem da prévia"
            onChange={(event) => void escolher(event.target.files?.[0])}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={ocupado}
            loading={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus aria-hidden /> Enviar imagem
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={ocupado}
            loading={gerar.isPending}
            aria-describedby="og-gerar-aviso"
            onClick={() => {
              onErro(null);
              gerar.mutate(ref, {
                onSuccess: () => toast({ title: 'Imagem gerada', tone: 'success' }),
                onError: falhou,
              });
            }}
          >
            <Sparkles aria-hidden /> Gerar imagem
          </Button>
          {page.ogImage === null ? null : (
            <Button
              variant="ghost"
              size="sm"
              disabled={ocupado}
              loading={remover.isPending}
              onClick={() => {
                onErro(null);
                remover.mutate(ref, { onError: falhou });
              }}
            >
              <Trash2 aria-hidden /> Remover imagem
            </Button>
          )}
          <p id="og-gerar-aviso" className="w-full text-2xs text-muted-foreground">
            Gerar imagem usa o título e a descrição já salvos.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** "Como aparece no WhatsApp": o cartão de link, com o rascunho e a imagem salva. */
function PreviaWhatsApp({
  workspaceId,
  page,
  titulo,
  descricao,
  publicUrl,
}: {
  workspaceId: string;
  page: RedirectorPage;
  titulo: string;
  descricao: string;
  publicUrl: string | null;
}) {
  const imagem = useRedirectorPageImage(workspaceId, page.redirectorId, page.ogImage !== null);
  const host = hostDe(publicUrl);
  return (
    <figure className="flex flex-col gap-1">
      <figcaption className="text-2xs text-muted-foreground">Como aparece no WhatsApp</figcaption>
      <div className="overflow-hidden rounded-lg border border-border bg-surface-subtle">
        {page.ogImage === null ? null : imagem.data?.dataUrl ? (
          <img
            src={imagem.data.dataUrl}
            alt="Imagem da prévia do link"
            className="aspect-[1200/630] w-full object-cover"
          />
        ) : (
          <Skeleton className="aspect-[1200/630] w-full" />
        )}
        <div className="flex flex-col gap-0.5 p-2">
          <span className="line-clamp-2 text-sm font-medium">{titulo.trim() || TITULO_PADRAO}</span>
          {descricao.trim() ? (
            <span className="line-clamp-2 text-2xs text-muted-foreground">{descricao}</span>
          ) : null}
          {host === null ? null : <span className="text-2xs text-muted-foreground">{host}</span>}
        </div>
      </div>
    </figure>
  );
}

/** A página no celular, em moldura — só ilustração; quem monta a de verdade é o servidor. */
function PreviaCelular({ titulo, descricao }: { titulo: string; descricao: string }) {
  return (
    <figure className="flex flex-col gap-1">
      <figcaption className="text-2xs text-muted-foreground">A página no celular</figcaption>
      <div
        className="mx-auto flex w-full max-w-56 flex-col items-center gap-2 rounded-xl border-4 border-border bg-surface p-4 text-center"
        aria-hidden
      >
        <span className="text-sm font-medium">{titulo.trim() || TITULO_PADRAO}</span>
        {descricao.trim() ? (
          <span className="line-clamp-3 text-2xs text-muted-foreground">{descricao}</span>
        ) : null}
        <span className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
          Entrar no grupo
        </span>
        <span className="flex items-center gap-1 text-2xs text-muted-foreground">
          <Copy className="size-3" /> Copiar link
        </span>
        <span className="text-2xs text-muted-foreground underline">Não abriu? Toque aqui</span>
        <span className="flex items-center gap-1 text-2xs text-muted-foreground">
          <QrCode className="size-3" /> no computador, QR
        </span>
      </div>
    </figure>
  );
}

/* -------------------------------------------------------------------------- */
/*  Placar do A/B (B2.6) — pronto do servidor                                  */
/* -------------------------------------------------------------------------- */

function PlacarAB({ workspaceId, redirectorId }: { workspaceId: string; redirectorId: string }) {
  const query = useRedirectorPageVariants(workspaceId, redirectorId, 30);
  return (
    <section className="flex flex-col gap-2 border-t border-border pt-4">
      <h3 className="text-md font-medium">Página × direto</h3>
      <p className="text-2xs text-muted-foreground">
        Últimos 30 dias, por clique. Conta como entrada só quem foi casado com uma entrada no grupo;
        quem ainda está na janela de espera não vale como entrada nem como desistência.
      </p>
      {query.isPending ? (
        <SkeletonText lines={3} />
      ) : query.isError ? (
        <ErrorState
          title="Não conseguimos montar a comparação"
          description={humanizeError(query.error)}
          onRetry={() => void query.refetch()}
        />
      ) : query.data.page.clicks === 0 && query.data.direct.clicks === 0 ? (
        <EmptyState
          kind="no-data"
          title="Nenhum clique no teste"
          description={leituraDoPlacar(query.data)}
        />
      ) : (
        <PlacarTabela placar={query.data} />
      )}
    </section>
  );
}

function PlacarTabela({ placar }: { placar: RedirectorPageVariants }) {
  return (
    <>
      <p className="text-sm">{leituraDoPlacar(placar)}</p>
      <ul className="flex flex-col divide-y divide-border">
        <LinhaDoPlacar row={placar.page} />
        <LinhaDoPlacar row={placar.direct} />
      </ul>
    </>
  );
}

function LinhaDoPlacar({ row }: { row: RedirectorPageVariantRow }) {
  const variante = VARIANTE[row.variant];
  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <Badge tone={row.variant === 'PAGE' ? 'brand' : 'outline'} title={variante.hint}>
        {variante.label}
      </Badge>
      <span className="nums text-sm">
        {formatNumber(row.clicks)} cliques · {formatNumber(row.matched)} entradas
      </span>
      {row.waiting > 0 ? (
        <span className="nums text-2xs text-muted-foreground">
          {formatNumber(row.waiting)} aguardando
        </span>
      ) : null}
      <span className="nums ml-auto text-sm font-medium">{taxaEmTexto(row)}</span>
      {row.confidence === null ? null : (
        <ConfidenceBadge confidence={row.confidence} score={row.avgScore} variant="inline" />
      )}
    </li>
  );
}
